import React, { useState, useEffect } from "react";
import {
  Star,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings as SettingsIcon,
  Globe,
  Mail,
  Users,
  TrendingUp,
  Calendar,
  Play,
  Download,
  UserPlus,
  X,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  fetchTrustpilotSettings,
  updateTrustpilotSettings,
  fetchTrustpilotStats,
  fetchTrustpilotHistory,
  triggerTrustpilotScheduler,
  sendTrustpilotInvitation,
  clearActionMessages,
} from "../../redux/Slices/TrustpilotSlice";
import { UpdateTrustpilotSettingsDto } from "../../redux/Actions/TrustpilotActions";

const TrustpilotSettings = () => {
  const dispatch = useAppDispatch();
  const {
    settings,
    settingsLoading,
    settingsError,
    stats,
    statsLoading,
    history,
    historyLoading,
    actionLoading,
    actionSuccess,
    actionError,
  } = useAppSelector((state) => state.trustpilot);

  const [localSettings, setLocalSettings] = useState<UpdateTrustpilotSettingsDto>({
    enabled: true,
    delayDays: 5,
    reviewPageUrl: "https://it.trustpilot.com/review/kokobeo.com",
    localeUrls: {},
  });

  const [activeTab, setActiveTab] = useState<"settings" | "history" | "stats">("settings");
  const [manualSendEmail, setManualSendEmail] = useState("");
  const [showManualSendModal, setShowManualSendModal] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchTrustpilotSettings());
    dispatch(fetchTrustpilotStats());
    dispatch(fetchTrustpilotHistory({ page: 1, limit: 50 }));
  }, [dispatch]);

  // Update local settings when Redux settings are loaded
  useEffect(() => {
    if (settings) {
      setLocalSettings({
        enabled: settings.enabled,
        delayDays: settings.delayDays,
        reviewPageUrl: settings.reviewPageUrl,
        localeUrls: settings.localeUrls,
      });
    }
  }, [settings]);

  // Show success/error toasts
  useEffect(() => {
    if (actionSuccess) {
      toast.success(actionSuccess);
      dispatch(clearActionMessages());
      // Refresh data after successful actions
      dispatch(fetchTrustpilotStats());
      dispatch(fetchTrustpilotHistory({ page: 1, limit: 50 }));
    }
    if (actionError) {
      toast.error(actionError);
      dispatch(clearActionMessages());
    }
  }, [actionSuccess, actionError, dispatch]);

  const handleSaveSettings = async () => {
    await dispatch(updateTrustpilotSettings(localSettings));
  };

  const handleTriggerScheduler = async () => {
    const result = await dispatch(triggerTrustpilotScheduler());
    if (triggerTrustpilotScheduler.fulfilled.match(result)) {
      // Refresh stats and history after successful trigger
      dispatch(fetchTrustpilotStats());
      dispatch(fetchTrustpilotHistory({ page: 1, limit: 20 }));
    }
  };

  const handleManualSend = async () => {
    if (!manualSendEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    const result = await dispatch(sendTrustpilotInvitation({ userIdOrEmail: manualSendEmail }));
    if (sendTrustpilotInvitation.fulfilled.match(result)) {
      // Refresh stats and history after successful send
      dispatch(fetchTrustpilotStats());
      dispatch(fetchTrustpilotHistory({ page: 1, limit: 20 }));
    }
    setManualSendEmail("");
    setShowManualSendModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader />
      <div className="lg:ml-64 pt-16 flex-1 flex flex-col">
        <div className="p-6 flex-1">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Star className="h-8 w-8 text-green-600" />
              <h1 className="text-3xl font-bold text-gray-900">Trustpilot Management</h1>
            </div>
            <p className="text-gray-600">
              Manage review invitations and track customer feedback on Trustpilot
            </p>
          </div>

          {/* Error Alert */}
          {settingsError && (
            <Alert className="bg-red-50 border-red-200 mb-6">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">{settingsError}</AlertDescription>
            </Alert>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab("settings")}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === "settings"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <SettingsIcon className="inline h-4 w-4 mr-2" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab("stats")}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === "stats"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <TrendingUp className="inline h-4 w-4 mr-2" />
              Statistics
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === "history"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Clock className="inline h-4 w-4 mr-2" />
              Invitation History
            </button>
          </div>

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              {/* Enable/Disable */}
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Enable Review Invitations</h3>
                    <p className="text-gray-600 text-sm">
                      Automatically send Trustpilot review invitations to customers
                    </p>
                  </div>
                  <Switch
                    checked={localSettings.enabled}
                    onCheckedChange={(checked) => setLocalSettings({ ...localSettings, enabled: checked })}
                  />
                </div>
              </Card>

              {/* Delay Days */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Invitation Timing</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Days After First Invoice
                    </label>
                    <Input
                      type="number"
                      value={localSettings.delayDays}
                      onChange={(e) =>
                        setLocalSettings({ ...localSettings, delayDays: parseInt(e.target.value) || 5 })
                      }
                      className="w-full"
                      min="1"
                      max="30"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Send invitation {localSettings.delayDays} days after customer receives their first invoice
                    </p>
                  </div>
                </div>
              </Card>

              {/* Review Page URL */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Trustpilot Review Page URL
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Page URL
                    </label>
                    <Input
                      type="url"
                      value={localSettings.reviewPageUrl}
                      onChange={(e) => setLocalSettings({ ...localSettings, reviewPageUrl: e.target.value })}
                      className="w-full"
                      placeholder="https://it.trustpilot.com/review/kokobeo.com"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      This is the URL where customers will be directed to leave their review
                    </p>
                  </div>
                </div>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleSaveSettings} 
                  disabled={actionLoading || settingsLoading} 
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  {actionLoading ? "Saving..." : "Save Settings"}
                </Button>
                <Button
                  onClick={handleTriggerScheduler}
                  disabled={actionLoading}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                  Trigger Scheduler Now
                </Button>
                <Button
                  onClick={() => setShowManualSendModal(true)}
                  disabled={actionLoading}
                  variant="outline"
                  className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                >
                  <UserPlus className="h-4 w-4" />
                  Manual Send Invitation
                </Button>
              </div>
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === "stats" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Sent</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statsLoading ? "..." : stats?.totalSent || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Send className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Last 30 Days</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statsLoading ? "..." : stats?.sentLast30Days || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Today</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statsLoading ? "..." : stats?.sentToday || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6 md:col-span-3">
                <h3 className="text-lg font-semibold mb-4">Quick Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Invitations are sent daily at 2:00 AM UTC</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span>Only first-time invoice recipients receive invitations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-purple-600" />
                    <span>
                      Current delay: {localSettings.delayDays} days after first invoice
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Invitation History</h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => dispatch(fetchTrustpilotHistory({ page: 1, limit: 50 }))}
                  disabled={historyLoading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {historyLoading ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">Loading...</p>
                </div>
              ) : !history?.data || history.data.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No invitations sent yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Invoice #</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Sent At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">{item.invoiceNumber || "N/A"}</td>
                          <td className="py-3 px-4">{item.recipientEmail}</td>
                          <td className="py-3 px-4">
                            {item.sentAt
                              ? new Date(item.sentAt).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {history && history.total > 0 && (
                <div className="flex items-center justify-between mt-4 px-4 py-3 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((history.page - 1) * history.limit) + 1} to{" "}
                    {Math.min(history.page * history.limit, history.total)} of{" "}
                    {history.total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (history.page > 1) {
                          dispatch(fetchTrustpilotHistory({ page: history.page - 1, limit: 20 }));
                        }
                      }}
                      disabled={history.page <= 1}
                      className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Page {history.page} of {Math.ceil(history.total / history.limit)}
                    </span>
                    <button
                      onClick={() => {
                        if (history.page < Math.ceil(history.total / history.limit)) {
                          dispatch(fetchTrustpilotHistory({ page: history.page + 1, limit: 20 }));
                        }
                      }}
                      disabled={history.page >= Math.ceil(history.total / history.limit)}
                      className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
        <SharedFooter3 />
      </div>

      {/* Manual Send Modal */}
      {showManualSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Card className="w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-green-600" />
                Send Manual Invitation
              </h3>
              <button
                onClick={() => {
                  setShowManualSendModal(false);
                  setManualSendEmail("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={manualSendEmail}
                  onChange={(e) => setManualSendEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="w-full"
                />
                <p className="text-sm text-gray-500 mt-2">
                  💡 <strong>Tip:</strong> Enter the user&apos;s email address. The system will find the user and send invitations to all user types.
                </p>
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  ✅ The system will send invitations to <strong>all user types</strong> (Clients, Professionals).
                  <br />
                  ✅ This bypasses the 5-day delay - use for testing or special cases.
                </AlertDescription>
              </Alert>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleManualSend}
                  disabled={actionLoading || !manualSendEmail.trim()}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {actionLoading ? "Sending..." : "Send Invitation"}
                </Button>
                <Button
                  onClick={() => {
                    setShowManualSendModal(false);
                    setManualSendEmail("");
                  }}
                  variant="outline"
                  disabled={actionLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TrustpilotSettings;
